// src/views/dashboard/money-flow/ClientDashboard.jsx
import React, { Component } from 'react';
import '../../../../styles/components/client-dashboard.scss';
import HeaderBanner from '../../../common/HeaderBanner';
import { SideBar } from '../../sidebar';
import i18n from '../../../../i18n';
import FilterSection from '../../../common/Filter';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import Select from 'react-select';
import DateRangeTags from '../../../common/DateRangeTags';
import { getGroupList } from '../Group/services/groupServices';
import {
  getCashflowAuctionPayment,
  getClientPayOutDetails,
  getClients,
  getWinners,
} from './services/cashFlowService';
import AuctionPaymentSummary from './components/AuctionPayment/AuctionPaymentSummary';
import { ArrowLeft } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import ClientPaymentSkeleton from './components/ClientPaymentSkeleton';

function detectTag(startDate, endDate) {
  if (!startDate || !endDate) return 'custom';
  const presets = ['7', '30', '90', '365'];
  for (const tag of presets) {
    const range = DateRangeTags.resolveRange(tag);
    if (range.startDate === startDate && range.endDate === endDate) return tag;
  }
  return 'custom';
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
class AuctionPaymentCashFlow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openModal: null,
      isLoading: false,
      hideSidebar: false,
      search: { startDate: '', endDate: '', client: null, group: [], bidder: null },
      clients: [],
      groups: [],
      bidders: [],
      clientLoading: false,
      groupLoading: false,
      bidderLoading: false,
      page: 1,
      limit: 15,
      dateTag: 'custom',
      auctionPaymentData: null,
      clientPayOutSummary: [],
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const urlStart = params.get('startDate');
    const urlEnd = params.get('endDate');

    let startDate, endDate, dateTag;

    if (urlStart && urlEnd) {
      // URL has explicit dates — restore them and detect which tag they match
      startDate = urlStart;
      endDate = urlEnd;
      dateTag = detectTag(urlStart, urlEnd);
    } else {
      // No URL dates — default to last 7 days
      dateTag = 'custom';
    }

    this.setState(
      {
        dateTag,
        search: {
          startDate,
          endDate,
          client: params.get('client') ? parseInt(params.get('client')) : null,

          group: params.get('group')
            ? params
                .get('group')
                .split(',')
                .map((id) => parseInt(id))
            : [],

          bidder: params.get('bidder') ? parseInt(params.get('bidder')) : null,
        },
        page: parseInt(params.get('page')) || 1,
        limit: parseInt(params.get('limit')) || 15,
      },
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
        this.fetchAuctionPayment();
        this.fetchClientPayOutDeatails();
      }
    );
  }

  // Update URL without refreshing page
  updateUrlQuery(q) {
    const queryString = new URLSearchParams(
      Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== '' && v !== null))
    ).toString();
    window.history.replaceState(null, '', `${window.location.pathname}?${queryString}`);
  }

  async fetchAuctionPayment() {
    this.setState({ isLoading: true });
    try {
      const { startDate, endDate, bidder, client, group } = this.state.search;
      const q = {};
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      if (bidder) q.bidder = bidder;
      if (client) q.client = client;
      if (group?.length) q.group = group.join(',');

      this.updateUrlQuery(q);

      const response = await getCashflowAuctionPayment(q); // your API model fn
      if (response.success) {
        this.setState({ auctionPaymentData: response.data, isLoading: false });
      } else {
        this.setState({ isLoading: false });
      }
    } catch {
      this.setState({ isLoading: false });
    }
  }

  async fetchClientPayOutDeatails() {
    this.setState({ isLoading: true });
    try {
      let { limit, page } = this.state;
      const { startDate, endDate, bidder, client, group } = this.state.search;
      const q = {
        page: page,
        limit: limit,
      };
      if (startDate) q.startDate = startDate;
      if (endDate) q.endDate = endDate;
      if (bidder) q.bidder = bidder;
      if (client) q.client = client;
      if (group?.length) q.group = group.join(',');

      this.updateUrlQuery(q);

      const response = await getClientPayOutDetails(q); // your API model fn
      if (response.success) {
        this.setState({
          clientPayOutSummary: response,
          isLoading: false,
        });
      } else {
        this.setState({ isLoading: false });
      }
    } catch {
      this.setState({ isLoading: false });
    }
  }

  async fetchClientList() {
    this.setState({ clientLoading: true });
    try {
      const response = await getClients();
      if (response.success) {
        const clients = [
          {
            value: null,
            label: i18n.t('all') || 'All',
          },
          ...response.data.map((item) => ({
            value: item.id,
            label: item.organization_name,
          })),
        ];

        this.setState({
          clients,
          clientLoading: false,
        });
      } else {
        this.setState({ clientLoading: false });
      }
    } catch {
      this.setState({ clientLoading: false });
    }
  }

  async fetchGroupList() {
    this.setState({ groupLoading: true });
    const { client } = this.state.search;
    const q = client ? { organization: client } : {};
    const response = await getGroupList(q);
    if (response.success) {
      const groups = [
        {
          value: null,
          label: i18n.t('all') || 'All',
          organization: null,
        },
        ...response.data.map((item) => ({
          value: item.id,
          label: item.group_name,
          organization: item.organization,
        })),
      ];

      this.setState({
        groups,
        groupLoading: false,
      });
    } else {
      this.setState({ groupLoading: false });
    }
  }

  async fetchBidderList() {
    this.setState({ bidderLoading: true });
    const { client, group } = this.state.search;

    const q = {};
    if (client) q.client = client;
    if (group?.length) q.group = group.join(',');

    const response = await getWinners(q);
    if (response.success) {
      const bidders = [
        {
          value: null,
          label: i18n.t('all') || 'All',
        },
        ...response.data.map((item) => ({
          value: item.id,
          label: `${item.name} (${item.id})`,
        })),
      ];

      this.setState({
        bidders,
        bidderLoading: false,
      });
    } else {
      this.setState({ bidderLoading: false });
    }
  }

  handleDateTag = (tag, range) => {
    this.setState((prev) => ({
      dateTag: tag,
      search: { ...prev.search, ...range },
    }));
  };

  //  Client onChange — store as integer, clear groups, load group list
  handleClientChange = (selectedOption) => {
    const clientValue = selectedOption ? parseInt(selectedOption.value) : null;
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          client: clientValue,
          group: [],
          bidder: null,
        },
        groups: [],
        bidders: [],
      }),
      async () => {
        await this.fetchGroupList();
        await this.fetchBidderList();
      }
    );
  };

  //  Group onChange — store as integer
  handleGroupChange = (selectedOptions) => {
    const selectedGroups = selectedOptions || [];

    // First selected group's organization becomes client
    const client = selectedGroups.length ? parseInt(selectedGroups[0].organization) : null;

    // Keep only groups from same organization
    const validGroups = selectedGroups.filter((item) => parseInt(item.organization) === client);

    const groupValues = validGroups.map((item) => parseInt(item.value));

    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          client,
          group: groupValues,
          bidder: null,
        },
        bidders: [],
      }),
      async () => {
        // Reload groups filtered by selected client
        await this.fetchGroupList();

        // Reload bidders
        await this.fetchBidderList();
      }
    );
  };

  //  Bidder onChange — store as integer
  handleBidderChange = (selectedOption) => {
    this.setState((prevState) => ({
      search: {
        ...prevState.search,
        bidder: selectedOption ? parseInt(selectedOption.value) : null,
      },
    }));
  };

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      dateTag: 'custom',
      search: { ...prev.search, [name]: value },
    }));
  };
  handleSearch = () => {
    this.fetchAuctionPayment();
    this.fetchClientPayOutDeatails();
  };

  handleClear = () => {
    const range = DateRangeTags.resolveRange('7');
    this.setState(
      {
        dateTag: '7',
        search: {
          startDate: range.startDate,
          endDate: range.endDate,
          client: null,
          group: [],
          bidder: null,
        },
      },
      () => {
        this.fetchClientList();
        this.fetchGroupList();
        this.fetchBidderList();
        this.fetchAuctionPayment();
        this.fetchClientPayOutDeatails();
      }
    );
  };

  render() {
    const {
      clients,
      clientLoading,
      groups,
      groupLoading,
      bidders,
      bidderLoading,
      search,
      hideSidebar,
      auctionPaymentData,
      clientPayOutSummary,
      isLoading,
    } = this.state;
    const { startDate, endDate } = search;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('auction_payment_summary')}
          subtitle={i18n.t('auction_payment_summary_subtitle')}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={
                  hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar ? (
                          <i className="icon-size-actual" />
                        ) : (
                          <i className="icon-size-fullscreen" />
                        )}
                      </span>
                    </div>
                    <div className="header-action">
                      {/* Back Button */}

                      <NavLink to="/cash-flow" className="button1 button1-gradient button1-back">
                        <ArrowLeft size={18} className="icon" />
                        <span>{i18n.t('back')}</span>
                      </NavLink>
                    </div>
                  </div>

                  <div className="card-body">
                    <DateRangeTags activeTag={this.state.dateTag} onChange={this.handleDateTag} />
                    <FilterSection
                      loading={this.state.isLoading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          value={startDate}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="startDate" className="floating-label">
                          {i18n.t('start_date')}
                        </label>
                      </div>
                      <div className="select-wrapper floating-label-wrapper">
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          value={endDate}
                          onChange={this.handleSearchChange}
                          className="filter-base floating-input"
                          placeholder=" "
                        />
                        <label htmlFor="endDate" className="floating-label">
                          {i18n.t('end_date')}
                        </label>
                      </div>
                      <div className="react-select-wrapper">
                        <Select
                          options={clients}
                          value={clients.find((c) => c.value === this.state.search.client) || null}
                          onChange={this.handleClientChange}
                          name="client"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={clientLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={clientLoading}
                          isDisabled={clientLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('client') || 'Client'}</label>
                      </div>

                      {/* Group Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={groups}
                          //  compare number === number
                          value={groups.filter((c) => this.state.search.group?.includes(c.value))}
                          onChange={this.handleGroupChange}
                          name="group"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={groupLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={groupLoading}
                          isDisabled={groupLoading}
                          isMulti
                          isClearable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                        />
                        <label className="react-select-label">{i18n.t('group_name')}</label>
                      </div>

                      {/* Bidder Dropdown */}
                      <div className="react-select-wrapper">
                        <Select
                          options={bidders}
                          //  compare number === number
                          value={bidders.find((c) => c.value === this.state.search.bidder) || null}
                          onChange={this.handleBidderChange}
                          name="bidder"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={bidderLoading ? `${i18n.t('loading')}...` : i18n.t('all')}
                          isLoading={bidderLoading}
                          isDisabled={bidderLoading}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('bidder')}</label>
                      </div>
                    </FilterSection>
                    {isLoading ? (
                      <ClientPaymentSkeleton />
                    ) : (
                      <AuctionPaymentSummary
                        data={auctionPaymentData}
                        search={search}
                        clientPayOutSummary={clientPayOutSummary}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

export default AuctionPaymentCashFlow;
